import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    LinearProgress,
    Snackbar,
    Paper,
    Divider
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    LocalHospital as LocalHospitalIcon,
    LocationCity as LocationCityIcon,
    AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import axios from 'axios';
import './BaseDataSyncPage.css';

const BaseDataSyncPage: React.FC = () => {
    const [syncResults, setSyncResults] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [loadingSingle, setLoadingSingle] = useState<string>('');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const syncConfigurations = [
        {
            key: 'doctors',
            title: 'Medici',
            description: 'Sincronizza tutti i medici dal sistema ReCup',
            icon: <PeopleIcon />,
            endpoint: '/api/base-data-sync/doctors',
            order: 1,
            color: '#1976d2'
        },
        {
            key: 'authorities',
            title: 'Authorities',
            description: 'Sincronizza le authorities dal sistema ReCup',
            icon: <AccountBalanceIcon />,
            endpoint: '/api/base-data-sync/authorities',
            order: 2,
            color: '#7b1fa2'
        },
        {
            key: 'sites',
            title: 'Sedi',
            description: 'Sincronizza le sedi dal sistema ReCup',
            icon: <LocationCityIcon />,
            endpoint: '/api/base-data-sync/sites',
            order: 3,
            color: '#388e3c'
        },
        {
            key: 'executionUnits',
            title: 'Unità Eroganti',
            description: 'Sincronizza le unità eroganti dal sistema ReCup',
            icon: <BusinessIcon />,
            endpoint: '/api/base-data-sync/execution-units',
            order: 4,
            color: '#f57c00'
        },
        {
            key: 'healthcareServices',
            title: 'Servizi Sanitari',
            description: 'Sincronizza i servizi sanitari dal sistema ReCup',
            icon: <LocalHospitalIcon />,
            endpoint: '/api/base-data-sync/healthcare-services',
            order: 5,
            color: '#d32f2f'
        }
    ];

    const handleSyncAll = async () => {
        setLoading(true);
        setSyncResults(null);
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/base-data-sync/all', {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 300000 // 5 minuti
            });

            setSyncResults(response.data.data);
            setSnackbar({
                open: true,
                message: response.data.message,
                severity: response.data.success ? 'success' : 'warning'
            });
        } catch (error: any) {
            console.error('Sync all failed:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Errore durante la sincronizzazione completa',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSyncSingle = async (config: any) => {
        setLoadingSingle(config.key);
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(config.endpoint, {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000 // 2 minuti
            });

            // Aggiorna i risultati per questo specifico tipo
            setSyncResults((prev: any) => ({
                ...prev,
                [config.key]: response.data.data
            }));

            setSnackbar({
                open: true,
                message: `${config.title}: ${response.data.message}`,
                severity: response.data.success ? 'success' : 'warning'
            });
        } catch (error: any) {
            console.error(`Sync ${config.key} failed:`, error);
            setSnackbar({
                open: true,
                message: `${config.title}: ${error.response?.data?.message || 'Errore durante la sincronizzazione'}`,
                severity: 'error'
            });
        } finally {
            setLoadingSingle('');
        }
    };

    const formatDuration = (duration: number) => {
        if (!duration) return 'N/A';
        return `${(duration / 1000).toFixed(1)}s`;
    };

    const getSeverityIcon = (errors: number, processed: number) => {
        if (errors === 0 && processed > 0) return <CheckCircleIcon color="success" />;
        if (errors > 0 && errors < processed) return <WarningIcon color="warning" />;
        if (errors > 0) return <ErrorIcon color="error" />;
        return null;
    };

    const renderSyncResult = (key: string, result: any, config: any) => {
        if (!result) return null;

        return (
            <Box sx={{ mb: 2 }}>
                <Paper sx={{ p: 2, borderLeft: `4px solid ${config.color}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {config.icon}
                        <Typography variant="h6" sx={{ ml: 1, mr: 2 }}>
                            {config.title}
                        </Typography>
                        {getSeverityIcon(result.errors, result.processed)}
                    </Box>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="body2" color="textSecondary">Processati</Typography>
                            <Typography variant="h6">{result.processed || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="body2" color="textSecondary">Creati</Typography>
                            <Typography variant="h6" color="success.main">{result.created || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="body2" color="textSecondary">Aggiornati</Typography>
                            <Typography variant="h6" color="info.main">{result.updated || 0}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="body2" color="textSecondary">Errori</Typography>
                            <Typography variant="h6" color="error.main">{result.errors || 0}</Typography>
                        </Grid>
                    </Grid>
                    
                    {result.duration && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                                Durata: {formatDuration(result.duration)}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    };

    return (
        <Box className="base-data-sync-page">
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Sincronizzazione Dati di Base
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Sincronizza i dati di base necessari per il corretto funzionamento delle prenotazioni.
                    È necessario eseguire questa sincronizzazione prima di sincronizzare gli appuntamenti.
                </Typography>
            </Box>

            {/* Alert informativo */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Importante:</strong> I dati di base (medici, unità eroganti, servizi sanitari) devono essere 
                    sincronizzati prima delle prenotazioni per evitare errori di integrità referenziale.
                    Si consiglia di eseguire la sincronizzazione completa in sequenza.
                </Typography>
            </Alert>

            {/* Pulsante sincronizzazione completa */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Sincronizzazione Completa
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Sincronizza tutti i dati di base nell'ordine corretto
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<PlayArrowIcon />}
                            onClick={handleSyncAll}
                            disabled={loading || !!loadingSingle}
                            sx={{ minWidth: '180px' }}
                        >
                            {loading ? 'Sincronizzando...' : 'Sincronizza Tutto'}
                        </Button>
                    </Box>
                    {loading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress />
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                Sincronizzazione in corso... Questo potrebbe richiedere alcuni minuti.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Sincronizzazioni individuali */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Sincronizzazioni Individuali
                    </Typography>
                    <Grid container spacing={2}>
                        {syncConfigurations
                            .sort((a, b) => a.order - b.order)
                            .map((config) => (
                            <Grid item xs={12} sm={6} md={4} key={config.key}>
                                <Paper 
                                    sx={{ 
                                        p: 2, 
                                        height: '100%',
                                        border: `1px solid ${config.color}20`,
                                        borderLeft: `4px solid ${config.color}`
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        {config.icon}
                                        <Typography variant="subtitle1" sx={{ ml: 1 }}>
                                            {config.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        {config.description}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onClick={() => handleSyncSingle(config)}
                                        disabled={loading || loadingSingle === config.key}
                                        startIcon={loadingSingle === config.key ? undefined : <PlayArrowIcon />}
                                    >
                                        {loadingSingle === config.key ? 'Sincronizzando...' : 'Sincronizza'}
                                    </Button>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Risultati */}
            {syncResults && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Risultati Sincronizzazione
                        </Typography>
                        
                        {syncResults.totalDuration && (
                            <Alert 
                                severity={syncResults.totalErrors > 0 ? 'warning' : 'success'} 
                                sx={{ mb: 2 }}
                            >
                                <Typography>
                                    Sincronizzazione completata in {formatDuration(syncResults.totalDuration)}.
                                    {syncResults.totalErrors > 0 && ` Ci sono stati ${syncResults.totalErrors} errori.`}
                                </Typography>
                            </Alert>
                        )}
                        
                        {syncConfigurations.map((config) => 
                            renderSyncResult(config.key, syncResults[config.key], config)
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Snackbar per notifiche */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BaseDataSyncPage;